import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getToken, saveToken, removeToken } from '../utils/authStorage';

const AuthContext = createContext(null);

function normalizeUserData(userData) {
  if (!userData || typeof userData !== 'object') return userData;
  const normalizedVipLevel = parseInt(userData.vip_level ?? userData.vipLevel ?? 0, 10) || 0;
  return {
    ...userData,
    vip_level: normalizedVipLevel,
    vipLevel: normalizedVipLevel,
  };
}

function getApiBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { protocol, hostname } = window.location;
  const isLocalDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname);
  return isLocalDev ? `${protocol}//${hostname}:5000/api` : '/api';
}

function consumeImpersonationHash() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  if (!hash.startsWith('#imp=')) return null;
  try {
    const payload = JSON.parse(atob(hash.slice(5)));
    if (!payload?.token || !payload?.user) return null;
    saveToken(payload.token, { sessionOnly: true });
    if (payload.adminToken) {
      sessionStorage.setItem('impersonation_admin_token', payload.adminToken);
    }
    sessionStorage.setItem('impersonation_user', JSON.stringify(payload.user));
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
    return payload.user;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  const fetchUser = useCallback(async (retryCount = 0) => {
    let timer;
    try {
      // On reset-password page, never auto-login — token will be cleared by page
      if (window.location.pathname.startsWith('/reset-password')) {
        removeToken();
        setLoading(false);
        return;
      }
      const impersonatedUser = consumeImpersonationHash();
      if (impersonatedUser) {
        setUser(prev => prev || impersonatedUser);
      }
      const token = getToken();
      if (!token) { setLoading(false); return; }

      // Parallel fetch with 15s timeout — Render cold start can take up to 30s
      const controller = new AbortController();
       timer = setTimeout(() => controller.abort(), 15000);
      const [userRes, balanceRes] = await Promise.all([
        api.get('/users/me',       { signal: controller.signal }),
        api.get('/wallet/balance', { signal: controller.signal }),
      ]);
      clearTimeout(timer);
      setUser(normalizeUserData(userRes.data));
      setBalance(balanceRes.data);
    } catch (err) {
      clearTimeout(timer); // clear the actual timer ref
      // Only logout on definitive 401 from server — NOT on network errors / timeouts
      if (err.response?.status === 401) {
        removeToken();
        setUser(null);
      } else if (err.name === 'CanceledError' || err.code === 'ECONNABORTED' || !err.response) {
        // Network error / timeout (Render cold start) — retry once after 5s, keep user logged in
        if (retryCount === 0 && getToken()) {
          console.warn('[Auth] Server slow/cold start — retrying in 5s...');
          setTimeout(() => fetchUser(1), 5000);
          return; // keep loading=true during retry
        }
        // After retry — keep user logged in, just stop loading
        console.warn('[Auth] Server unreachable after retry — keeping session alive');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Listen for 401 events from api.js interceptor — avoids hard reload
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setBalance(null);
      setLoading(false);
      window.history.pushState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    // 'auth:force-logout' = dispatched by WebSocketContext on ban/suspend WS event
    // 'auth:logout-required' = dispatched by api.js interceptor on 401
    window.addEventListener('auth:logout-required', handler);
    window.addEventListener('auth:force-logout', handler);
    return () => {
      window.removeEventListener('auth:logout-required', handler);
      window.removeEventListener('auth:force-logout', handler);
    };
  }, []);

  useEffect(() => {
    const statusHandler = (event) => {
      const nextStatus = event.detail?.status;
      if (!nextStatus) return;
        setUser(prev => (prev ? normalizeUserData({ ...prev, status: nextStatus }) : prev));
    };
    window.addEventListener('auth:user-status', statusHandler);
    return () => window.removeEventListener('auth:user-status', statusHandler);
  }, []);

  useEffect(() => {
    const vipUpgradeHandler = (event) => {
      const nextLevel = parseInt(event.detail?.new_level ?? event.detail?.newLevel, 10);
      if (!Number.isFinite(nextLevel)) return;
      setUser(prev => (prev ? normalizeUserData({ ...prev, vip_level: nextLevel, vipLevel: nextLevel }) : prev));
    };
    window.addEventListener('auth:vip-upgrade', vipUpgradeHandler);
    return () => window.removeEventListener('auth:vip-upgrade', vipUpgradeHandler);
  }, []);

  // login — save token + user, navigate immediately, fetch balance in background
  const login = async (token, userData, _rememberMe = true, options = {}) => {
    saveToken(token, options);
    setUser(normalizeUserData(userData));
    // Set a flag so api.js interceptor doesn't fire 401-logout during navigation
    window.__justLoggedIn = true;
    setTimeout(() => { delete window.__justLoggedIn; }, 3000);
    // Fetch balance in background — don't block navigation
    api.get('/wallet/balance').then(r => setBalance(r.data)).catch(() => {});
  };

  const logout = async () => {
    // Grab token before clearing — needed for the API call
    const token = getToken();
    // Clear state + token immediately — prevents any re-fetch on 401
    setUser(null);
    setBalance(null);
    setLoading(false);
    removeToken();
    sessionStorage.removeItem('impersonation_admin_token');
    sessionStorage.removeItem('impersonation_user');
    // Fire-and-forget: invalidate server session (non-blocking)
    // Use fetch directly so we don't need the axios token interceptor
    if (token) {
      fetch(`${getApiBase()}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).catch(() => {});
    }
  };

  const refreshBalance = async () => {
    try {
      const res = await api.get('/wallet/balance');
      setBalance(res.data);
      return res.data;
    } catch {}
  };

  const updateBalance = useCallback((newBalance) => setBalance(newBalance), []);

  // refreshUser — re-fetches user profile from server (e.g. after referral_code loads)
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/users/me');
      setUser(normalizeUserData(res.data));
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) { removeToken(); setUser(null); }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, balance, login, logout, refreshBalance, updateBalance, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
